import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { apiRequest } from '@/api/client';
import {
  claus,
  query,
  useActivacions,
  useCercaServeis,
  useZones,
  type ActivacioFiltres,
} from '@/api/activacions';
import type { ActivacioResumDto, EstatActivacio, Pagina } from '@/api/types';
import { Desplegable, type OpcioDesplegable } from '@/components/Desplegable';
import { Icona, type NomIcona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Permis } from '@/permisos/Permis';
import { useMe } from '@/permisos/MeContext';
import { BadgeTipus, Buit, ProgresConfirmacions } from './comu';
import {
  colorConfirmacions,
  iniciDies,
  minutsDes,
  PARAM_PESTANYA,
  textFa,
  useTempsRealTauler,
} from './suport';
import { W04DetallActivacio } from './W04DetallActivacio';

const MIDA = 25;
/** Mida de cada consulta quan una vista agrupa diversos estats (obertes, tancades). */
const MIDA_GRUP = 100;
const RETARD_CERCA_MS = 300;
const PERIODES = ['7', '30', '90', 'tots'] as const;
const ESTATS: EstatActivacio[] = [
  'OBERTA',
  'EN_CURS',
  'RESOLTA',
  'MITIGADA',
  'CANCELLADA',
  'FINALITZADA',
];
const OBERTS: EstatActivacio[] = ['OBERTA', 'EN_CURS'];
const TANCATS: EstatActivacio[] = ['RESOLTA', 'MITIGADA', 'CANCELLADA', 'FINALITZADA'];

type Periode = (typeof PERIODES)[number];
type TipusFiltre = '' | 'AVIS' | 'INCIDENCIA' | 'CRITICA';
/** Xips ràpids d'estat: obertes (per defecte), tancades o totes. */
type GrupEstat = 'obertes' | 'tancades' | '';

function csv(valors: (string | number | null | undefined)[]): string {
  return valors.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';');
}

/** Desplegable de filtre amb aspecte de xip, amb icona opcional. */
function ChipSelect({
  etiqueta,
  valor,
  actiu,
  icona,
  onChange,
  opcions,
}: {
  etiqueta: string;
  valor: string;
  actiu: boolean;
  icona?: NomIcona;
  onChange: (v: string) => void;
  opcions: OpcioDesplegable<string>[];
}) {
  return (
    <span className={`chip-select-wrap${icona ? ' chip-select-wrap--icona' : ''}`}>
      {icona ? <Icona nom={icona} className={actiu ? 'c-white' : undefined} /> : null}
      <Desplegable
        variant="chip"
        actiu={actiu}
        aria-label={etiqueta}
        valor={valor}
        opcions={opcions}
        onCanvia={onChange}
      />
    </span>
  );
}

/**
 * Llista per a diversos estats alhora: l'API només filtra per un estat, així que es fa una
 * consulta per estat i es combinen (sense paginació; les obertes són poques).
 */
function useLlistaAgrupada(filtres: ActivacioFiltres, estats: EstatActivacio[], actiu: boolean) {
  const consultes = useQueries({
    queries: estats.map((estat) => {
      const f = { ...filtres, estat, page: 0, size: MIDA_GRUP };
      return {
        queryKey: claus.llista(f),
        queryFn: () => apiRequest<Pagina<ActivacioResumDto>>(`/activacions${query(f)}`),
        enabled: actiu,
      };
    }),
  });
  // Combinació barata (com a molt uns centenars d'elements): es recalcula a cada render.
  const vistos = new Map<number, ActivacioResumDto>();
  for (const c of consultes) {
    for (const a of c.data?.items ?? []) if (estats.includes(a.estat)) vistos.set(a.id, a);
  }
  const items = [...vistos.values()].sort((x, y) => {
    if (x.critica !== y.critica) return x.critica ? -1 : 1;
    return y.createdAt.localeCompare(x.createdAt);
  });
  return {
    items,
    isLoading: consultes.some((c) => c.isLoading),
    isError: consultes.length > 0 && consultes.every((c) => c.isError),
    refetch: () => consultes.forEach((c) => void c.refetch()),
  };
}

/**
 * W-03 Activacions (mestre-detall): llista amb cerca, xips de filtre ràpid i filtres avançats
 * a l'esquerra; el detall (W-04) de l'activació triada a la dreta. A pantalles estretes la
 * llista i el detall s'apilen, i en triar-ne una només es mostra el detall.
 */
export function W03Activacions() {
  const { t } = useTranslation('operacio');
  const tc = useTranslation('comu').t;
  const f = useFormat();
  const { id: idParam } = useParams();
  const triada = idParam != null ? Number(idParam) : null;
  const [params] = useSearchParams();
  const { me } = useMe();
  const [ara] = useState(() => new Date());
  const [grup, setGrup] = useState<GrupEstat>(() => (params.get('text') ? '' : 'obertes'));
  const [crisi, setCrisi] = useState(false);
  const [meves, setMeves] = useState(false);
  const [mesFiltres, setMesFiltres] = useState(false);
  const [periode, setPeriode] = useState<Periode>('tots');
  const [tipus, setTipus] = useState<TipusFiltre>('');
  const [areaId, setAreaId] = useState('');
  const [serveiId, setServeiId] = useState('');
  const [zonaId, setZonaId] = useState('');
  const [estat, setEstat] = useState<'' | EstatActivacio>('');
  const [text, setText] = useState(() => params.get('text') ?? '');
  const [textCerca, setTextCerca] = useState(text);
  const [page, setPage] = useState(0);
  const serveis = useCercaServeis('');
  const zones = useZones();
  useTempsRealTauler();

  // La cerca del Centre de control arriba per ?text=… (ajust d'estat durant el render).
  const textUrl = params.get('text');
  const [textUrlAnterior, setTextUrlAnterior] = useState(textUrl);
  if (textUrl !== textUrlAnterior) {
    setTextUrlAnterior(textUrl);
    setText(textUrl ?? '');
    setTextCerca(textUrl ?? '');
    setGrup('');
    setPage(0);
  }

  // Debounce: no es llança una consulta per cada tecla.
  useEffect(() => {
    const h = setTimeout(() => setTextCerca(text), RETARD_CERCA_MS);
    return () => clearTimeout(h);
  }, [text]);

  const base = useMemo<ActivacioFiltres>(
    () => ({
      areaTipus: crisi ? 'CRISI' : undefined,
      desde: periode === 'tots' ? undefined : iniciDies(Number(periode), ara),
      tipus: tipus === 'AVIS' || tipus === 'INCIDENCIA' ? tipus : undefined,
      critica: tipus === 'CRITICA' ? true : undefined,
      areaId: areaId ? Number(areaId) : undefined,
      serveiId: serveiId ? Number(serveiId) : undefined,
      zonaId: zonaId ? Number(zonaId) : undefined,
      personaId: meves && me ? me.id : undefined,
      text: textCerca.trim() || undefined,
    }),
    [crisi, periode, tipus, areaId, serveiId, zonaId, meves, me, textCerca, ara],
  );
  // Un estat concret dels filtres avançats té prioritat sobre els xips Obertes / Tancades.
  const estatsGrup = estat
    ? null
    : grup === 'obertes'
      ? OBERTS
      : grup === 'tancades'
        ? TANCATS
        : null;
  const agrupada = useLlistaAgrupada(base, estatsGrup ?? [], estatsGrup != null);
  const paginada = useActivacions(
    { ...base, estat: estat || undefined, page, size: MIDA },
    estatsGrup == null,
  );
  const items: ActivacioResumDto[] = estatsGrup ? agrupada.items : (paginada.data?.items ?? []);
  const total = estatsGrup ? agrupada.items.length : (paginada.data?.total ?? 0);
  const pagines = estatsGrup ? 1 : Math.max(1, Math.ceil(total / MIDA));
  const carregant = estatsGrup ? agrupada.isLoading : paginada.isLoading;
  const error = estatsGrup ? agrupada.isError : paginada.isError;
  const reintenta = () => (estatsGrup ? agrupada.refetch() : void paginada.refetch());

  const canvia =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(0);
    };
  const commutaGrup = (g: Exclude<GrupEstat, ''>) => {
    setGrup((actual) => (actual === g ? '' : g));
    setEstat('');
    setPage(0);
  };
  const filtresAvancats = [periode !== 'tots', tipus, areaId, serveiId, zonaId, estat].filter(
    Boolean,
  ).length;

  const exporta = () => {
    const capcalera = csv([
      t('columnes.codi'),
      t('columnes.tipus'),
      t('columnes.titol'),
      t('columnes.area'),
      t('columnes.servei'),
      t('columnes.zona'),
      t('columnes.confirmacions'),
      t('columnes.estat'),
      t('columnes.obert'),
    ]);
    const files = items.map((a) =>
      csv([
        a.codi,
        a.critica ? tc('tipusActivacio.CRITICA') : tc(`tipusActivacio.${a.tipus}`),
        a.titol,
        a.area,
        a.servei,
        a.zona,
        `${a.confirmats}/${a.total}`,
        tc(`estatActivacio.${a.estat}`),
        f.dataHora(a.createdAt),
      ]),
    );
    const bom = String.fromCharCode(0xfeff);
    const blob = new Blob([`${bom}${[capcalera, ...files].join('\n')}`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'activacions.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const serveisUnics = useMemo(
    () => (serveis.data ?? []).filter((s) => s.serveiId != null),
    [serveis.data],
  );

  return (
    <section
      className="pantalla mestre"
      data-screen="W-03"
      data-amb-detall={triada != null ? 'true' : undefined}
      // Amb la pestanya Xat del detall oberta la pantalla no es desplaça: només ho fa la conversa,
      // i la caixa per escriure queda sempre visible a baix (vegeu `.mestre[data-xat]`).
      data-xat={triada != null && params.get(PARAM_PESTANYA) === 'xat' ? 'true' : undefined}
    >
      <section className="mestre__llista" aria-labelledby="titol-activacions">
        <div className="mestre__cap">
          <h1 id="titol-activacions">{t('titols.activacions')}</h1>
          <div className="spacer" />
          <button
            type="button"
            className="btn btn--s btn--icona"
            onClick={exporta}
            disabled={!items.length}
            aria-label={t('llista.exporta')}
            title={t('llista.exporta')}
          >
            <Icona nom="doc-export" />
          </button>
          <Permis recurs="cai.activacions" tipus="ALTA">
            <Link className="btn btn--primary btn--s" to="/activacions/nova">
              <Icona nom="plus" />
              {t('llista.nova')}
            </Link>
          </Permis>
        </div>
        <label className="search">
          <Icona nom="magnifier" />
          <input
            type="search"
            placeholder={t('llista.cerca')}
            aria-label={t('llista.cercaEtiqueta')}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setPage(0);
            }}
          />
        </label>
        <div className="chips" role="group" aria-label={t('llista.filtresRapids')}>
          <button
            type="button"
            className="chip"
            aria-pressed={grup === 'obertes' && !estat}
            onClick={() => commutaGrup('obertes')}
          >
            {grup === 'obertes' && !estat && !carregant
              ? t('llista.chip.obertesN', { count: items.length })
              : t('llista.chip.obertes')}
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={meves}
            onClick={() => canvia(setMeves)(!meves)}
          >
            {t('llista.chip.meves')}
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={crisi}
            onClick={() => canvia(setCrisi)(!crisi)}
          >
            {tc('tipusArea.CRISI')}
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={grup === 'tancades' && !estat}
            onClick={() => commutaGrup('tancades')}
          >
            {t('llista.chip.tancades')}
          </button>
          <button
            type="button"
            className="chip"
            aria-expanded={mesFiltres}
            aria-controls="filtres-avancats"
            onClick={() => setMesFiltres((v) => !v)}
          >
            <Icona nom="filter" />
            {filtresAvancats
              ? t('llista.chip.filtresN', { count: filtresAvancats })
              : t('llista.chip.filtres')}
          </button>
        </div>
        {mesFiltres ? (
          <div className="chips filtres-avancats" id="filtres-avancats">
            <ChipSelect
              etiqueta={t('llista.periode')}
              icona="calendar"
              valor={periode}
              actiu={periode !== 'tots'}
              onChange={(v) => canvia(setPeriode)(v as Periode)}
              opcions={PERIODES.map((p) => ({ valor: p, etiqueta: t(`llista.periodes.${p}`) }))}
            />
            <ChipSelect
              etiqueta={t('columnes.tipus')}
              valor={tipus}
              actiu={tipus !== ''}
              onChange={(v) => canvia(setTipus)(v as TipusFiltre)}
              opcions={[
                { valor: '', etiqueta: t('llista.chip.tipus', { valor: t('llista.chip.tots') }) },
                ...(['AVIS', 'INCIDENCIA', 'CRITICA'] as const).map((x) => ({
                  valor: x,
                  etiqueta: tc(`tipusActivacio.${x}`),
                })),
              ]}
            />
            <ChipSelect
              etiqueta={t('columnes.area')}
              valor={areaId}
              actiu={areaId !== ''}
              onChange={canvia(setAreaId)}
              opcions={[
                { valor: '', etiqueta: t('llista.chip.area', { valor: t('llista.chip.totes') }) },
                ...(me?.arees ?? []).map((a) => ({ valor: String(a.id), etiqueta: a.nom })),
              ]}
            />
            <ChipSelect
              etiqueta={t('columnes.servei')}
              valor={serveiId}
              actiu={serveiId !== ''}
              onChange={canvia(setServeiId)}
              opcions={[
                { valor: '', etiqueta: t('llista.chip.servei', { valor: t('llista.chip.tots') }) },
                ...serveisUnics.map((s) => ({
                  valor: String(s.serveiId ?? ''),
                  etiqueta: s.serveiNom ?? '',
                })),
              ]}
            />
            <ChipSelect
              etiqueta={t('columnes.zona')}
              valor={zonaId}
              actiu={zonaId !== ''}
              onChange={canvia(setZonaId)}
              opcions={[
                { valor: '', etiqueta: t('llista.chip.zona', { valor: t('llista.chip.totes') }) },
                ...(zones.data ?? []).map((z) => ({ valor: String(z.id), etiqueta: z.nom })),
              ]}
            />
            <ChipSelect
              etiqueta={t('columnes.estat')}
              valor={estat}
              actiu={estat !== ''}
              onChange={(v) => canvia(setEstat)(v as '' | EstatActivacio)}
              opcions={[
                { valor: '', etiqueta: t('llista.chip.estat', { valor: t('llista.chip.tots') }) },
                ...ESTATS.map((e) => ({ valor: e, etiqueta: tc(`estatActivacio.${e}`) })),
              ]}
            />
          </div>
        ) : null}
        <div className="mestre__items">
          {error ? (
            <ErrorCarrega onReintenta={reintenta} />
          ) : items.length === 0 ? (
            <Buit icona="tray">{carregant ? tc('sessio.carregant') : t('llista.buida')}</Buit>
          ) : (
            <ul className="mestre__ul">
              {items.map((a) => (
                <li key={a.id}>
                  <Link
                    className={`mestre__item${a.critica ? ' is-critica' : ''}`}
                    to={`/activacions/${a.id}`}
                    aria-current={a.id === triada ? 'true' : undefined}
                    data-activacio={a.id}
                  >
                    <span className="hstack gap-8">
                      <BadgeTipus tipus={a.tipus} critica={a.critica} />
                      {a.areaTipus === 'CRISI' ? (
                        <span className="badge badge--crisi">{tc('tipusArea.CRISI')}</span>
                      ) : null}
                      <span className="codi">{a.codi}</span>
                      <span className="spacer" />
                      <span className="dim t-caption nowrap" title={f.dataHora(a.createdAt)}>
                        {textFa(minutsDes(a.createdAt, ara.getTime()) ?? 0, t)}
                      </span>
                    </span>
                    <strong className="mestre__titol">{a.titol}</strong>
                    <span className="dim t-caption">
                      {[a.area, a.servei, tc(`estatActivacio.${a.estat}`)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <ProgresConfirmacions
                      confirmats={a.confirmats}
                      total={a.total}
                      color={colorConfirmacions(a)}
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="paginacio">
          <span>{t('llista.mostrant', { count: items.length, total })}</span>
          {pagines > 1 ? (
            <>
              <div className="spacer" />
              <button
                type="button"
                className="btn btn--xs"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('llista.anterior')}
              </button>
              <span>{t('llista.pagina', { page: page + 1, total: pagines })}</span>
              <button
                type="button"
                className="btn btn--xs"
                disabled={page + 1 >= pagines}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('llista.seguent')}
              </button>
            </>
          ) : null}
        </div>
      </section>
      <div className="mestre__detall">
        {triada != null ? (
          <W04DetallActivacio key={triada} id={triada} />
        ) : (
          <div className="mestre__buit">
            <Buit icona="exclamationmark-triangle">
              <strong>{t('llista.capTriada')}</strong>
              <br />
              {t('llista.capTriadaText')}
            </Buit>
          </div>
        )}
      </div>
    </section>
  );
}
